// Flow 2 - Pulse (docs/phase-0/slice6-ux-flows.md §4; slice6-component-designs.md §4).
// The `/apps/:appId/pulse` `AppDetail` sub-nav tab - reads
// `useOutletContext<AppDetailContext>()` exactly as `AppExplained`/`AppBuildTab` do
// (UX §2.1), no new route guard beyond the inherited cross-tenant gate. Read-only for
// every role (owner/admin/viewer alike): Pulse has no owner/admin-only action this
// slice (UX §4.4's own "Restricted (viewer)" row, "no control is disabled here"), so
// this screen never branches on role.
//
// `<h1>` is the app's own name, bare (component-designs.md §2.1: "Money/Pulse/Feedback
// are AppDetail sub-nav tabs, never their own shell"); the copywriter's own committed
// heading ("Your seven metrics", `pulse.metrics.heading`) is the section `<h2>` above
// the grid, "Churn signals" the second `<h2>` (UX §4.6).
//
// Churn signals ALWAYS render empty this build - see `../../api/pulse.ts`'s header
// comment for why: no landed wire exposes `AnomalySignal[]`/`ChurnSignalKind` back to
// the client, only the metrics themselves. `PulseChurnSignalCard` is built and directly
// unit-tested ready to wire once that read-back route lands.
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AnomalySignal, PulseView } from "@nightcrew/shared";
import type { AppDetailContext } from "../AppDetail.js";
import { PulseChurnSignalCard } from "../../components/PulseChurnSignalCard.js";
import { PulseMetricGrid } from "../../components/PulseMetricGrid.js";
import { getPulse } from "../../api/pulse.js";
import { pulseCopy } from "../../copy/pulse.js";

type PulseLoad =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly view: PulseView };

/** No landed wire source this build (see `../../api/pulse.ts`'s header) - kept as one
 * typed constant, never a literal `[]` scattered inline, so the single reason lives in
 * one place. */
const NO_CHURN_SIGNALS: readonly AnomalySignal[] = [];

export const PulseTab = (): React.JSX.Element => {
  const { app } = useOutletContext<AppDetailContext>();
  const [load, setLoad] = useState<PulseLoad>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const cancelled = { current: false };
    setLoad({ status: "loading" });
    void (async () => {
      const result = await getPulse(app.id);
      if (cancelled.current) return;
      if (isErr(result)) {
        setLoad({ status: "error" });
        return;
      }
      setLoad({ status: "ready", view: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [app.id, attempt]);

  const retry = (): void => {
    setAttempt((a) => a + 1);
  };

  if (load.status === "loading") {
    return (
      <div className="wrap">
        <h1 className="h2">{app.name}</h1>
        <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
          {pulseCopy("pulse.loading.status")}
        </p>
        <div className="pulse-metric-grid mt16">
          {MetricNameSkeletonKeys.map((key) => (
            <div key={key} className="card skeleton-card" />
          ))}
        </div>
      </div>
    );
  }

  if (load.status === "error") {
    return (
      <div className="wrap">
        <h1 className="h2">{app.name}</h1>
        <div className="card state-card mt16">
          <p>{pulseCopy("pulse.errors.body")}</p>
          <button type="button" className="btn btn-primary" onClick={retry}>
            {pulseCopy("pulse.errors.retryCta")}
          </button>
        </div>
      </div>
    );
  }

  const { view } = load;

  return (
    <div className="wrap">
      <h1 className="h2">{app.name}</h1>
      <span className="sr-only" aria-live="polite">
        {pulseCopy("pulse.loaded.status")}
      </span>

      {view.metrics.length === 0 ? (
        <p className="muted mt16" data-testid="pulse-empty-intro">
          {pulseCopy("pulse.metrics.emptyIntro", { appName: app.name })}
        </p>
      ) : null}

      <h2 className="h3 mt16">{pulseCopy("pulse.metrics.heading")}</h2>
      <div className="mt8">
        <PulseMetricGrid points={view.metrics} appStage={app.stage} />
      </div>

      <h2 className="h3 mt24">{pulseCopy("pulse.churnSignals.heading")}</h2>
      <div className="card pad mt8" data-testid="pulse-churn-signals">
        {NO_CHURN_SIGNALS.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            {pulseCopy("pulse.churnSignals.empty")}
          </p>
        ) : (
          NO_CHURN_SIGNALS.map((signal) => (
            <PulseChurnSignalCard key={`${signal.kind}-${signal.detectedAt}`} signal={signal} />
          ))
        )}
      </div>
    </div>
  );
};

/** Seven skeleton keys for the loading grid - a plain index tuple, not `MetricName.
 * options` (the real enum is for the populated grid's own ordering; the skeleton has no
 * per-metric identity to preserve). */
const MetricNameSkeletonKeys = [0, 1, 2, 3, 4, 5, 6] as const;
